import { z } from "zod";
import { competenciaSchema, dataSchema, somarMeses } from "./competencia";

/** Categorias de Pessoal que a folha do mês usa (criadas na primeira folha, se faltarem). */
export const CATEGORIAS_DA_FOLHA = {
  producao: { nome: "Salários — produção", cor: "#2457ff" },
  administrativo: { nome: "Salários — administrativo", cor: "#3b82f6" },
  proLabore: { nome: "Pró-labore", cor: "#1d4ed8" },
  encargos: { nome: "Encargos (INSS e FGTS)", cor: "#6366f1" },
  decimoEFerias: { nome: "13º e férias", cor: "#7c3aed" },
} as const;
export type ParteDaFolha = keyof typeof CATEGORIAS_DA_FOLHA;

/** 1/12 de 13º + 1/12 de férias com o terço (4/3) + FGTS de 8% sobre isso ≈ 21% dos salários. */
export const TAXA_DE_PROVISAO_PADRAO_BPS = 2100;

const centavos = z.number().int().min(0).max(100_000_000);

export const folhaDoMesSchema = z.object({
  competencia: competenciaSchema,
  salariosProducaoCents: centavos,
  salariosAdministrativoCents: centavos,
  proLaboreCents: centavos,
  inssCents: centavos,
  fgtsCents: centavos,
  decimoTerceiroEFeriasCents: centavos,
  vencimentoDosSalarios: dataSchema.nullable(),
  vencimentoDasGuias: dataSchema.nullable(),
});
export type FolhaDoMesInput = z.infer<typeof folhaDoMesSchema>;

export type ItemDaFolha = { chave: string; parte: ParteDaFolha; descricao: string; valorCents: number; vencimento: string | null };

/** Os lançamentos que a folha gera, um por valor preenchido. A chave liga o lançamento à parte da folha. */
export function itensDaFolha(f: FolhaDoMesInput, rotuloDoMes: string): ItemDaFolha[] {
  const itens: ItemDaFolha[] = [
    { chave: "producao", parte: "producao", descricao: `Salários da produção · ${rotuloDoMes}`, valorCents: f.salariosProducaoCents, vencimento: f.vencimentoDosSalarios },
    { chave: "administrativo", parte: "administrativo", descricao: `Salários do administrativo · ${rotuloDoMes}`, valorCents: f.salariosAdministrativoCents, vencimento: f.vencimentoDosSalarios },
    { chave: "proLabore", parte: "proLabore", descricao: `Pró-labore · ${rotuloDoMes}`, valorCents: f.proLaboreCents, vencimento: f.vencimentoDosSalarios },
    { chave: "inss", parte: "encargos", descricao: `Guia do INSS · ${rotuloDoMes}`, valorCents: f.inssCents, vencimento: f.vencimentoDasGuias },
    { chave: "fgts", parte: "encargos", descricao: `Guia do FGTS · ${rotuloDoMes}`, valorCents: f.fgtsCents, vencimento: f.vencimentoDasGuias },
    { chave: "decimoEFerias", parte: "decimoEFerias", descricao: `13º e férias · ${rotuloDoMes}`, valorCents: f.decimoTerceiroEFeriasCents, vencimento: f.vencimentoDosSalarios },
  ];
  return itens;
}

/** Vencimentos sugeridos: salários no dia 5 e guias no dia 20 do mês seguinte. */
export function vencimentosSugeridos(competencia: string) {
  const seguinte = somarMeses(competencia, 1);
  return { vencimentoDosSalarios: `${seguinte}-05`, vencimentoDasGuias: `${seguinte}-20` };
}

export const provisaoDoMes = (salariosCents: number, taxaBps: number) => Math.round((salariosCents * taxaBps) / 10_000);
